import {
  Blob,
  BlobStack,
  BuddyMark,
  Button,
  Card,
  CardHeader,
  CardTitle,
  ChatIcon,
  CheckIcon,
  ClockIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  Field,
  FieldLabel,
  GlobeIcon,
  HomeIcon,
  IconTile,
  InitialsAvatar,
  Input,
  Kbd,
  MailIcon,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Meter,
  MoreIcon,
  NavGroup,
  NavItem,
  OrgMark,
  PlusIcon,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverRow,
  PopoverTrigger,
  RailItem,
  RowText,
  SectionLabel,
  Segmented,
  SettingsIcon,
  SheetIcon,
  Switch,
  Tab,
  Tabs,
  TabsList,
  Tag,
  TasksIcon,
  TeamIcon,
  Tray,
  TrayRow,
  WorkingArc,
} from "@buddy/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

// A living sheet of every kit component, for checking the kit against the Paper
// "00 Design system" board. Not linked from the app.
export const Route = createFileRoute("/kit")({ component: Kit });

const levels = [
  { value: "act", label: "Act" },
  { value: "preapproved", label: "Pre-approved" },
  { value: "ask", label: "Ask" },
  { value: "hand_off", label: "Hand off" },
] as const;

function Kit() {
  const [level, setLevel] = useState<(typeof levels)[number]["value"]>("ask");
  return (
    <div className="flex min-h-full gap-2 bg-app p-2">
      <nav className="flex w-12 shrink-0 flex-col items-center gap-2 py-2">
        <BuddyMark />
        <div className="h-3" />
        <RailItem label="Home" href="#">
          <HomeIcon />
        </RailItem>
        <RailItem label="Chats" href="#" active>
          <ChatIcon />
        </RailItem>
        <RailItem label="Tasks" href="#">
          <TasksIcon />
        </RailItem>
        <RailItem label="Team" href="#">
          <TeamIcon />
        </RailItem>
        <div className="flex-1" />
        <RailItem label="Settings" href="#">
          <SettingsIcon />
        </RailItem>
        <InitialsAvatar name="Tyrese Young" />
      </nav>

      <Card className="w-[280px] shrink-0 gap-1.5 px-2 py-2.5">
        <div className="flex h-9 items-center gap-2.5 px-1.5">
          <OrgMark />
          <span className="text-[15px] font-medium leading-[18px]">Pallo Studio</span>
        </div>
        <Button variant="primary" className="mt-1.5 h-[38px] justify-start gap-2.5 pl-3 pr-1.5">
          <PlusIcon />
          <span className="flex-1 text-left">New chat</span>
          <span className="flex gap-[3px]">
            <Kbd onAccent>Ctrl</Kbd>
            <Kbd onAccent>N</Kbd>
          </span>
        </Button>
        <NavGroup label="Needs you">
          <NavItem href="#" leading={<Blob hue="amber" face="glasses" />} meta="9m" metaTone="attention">
            Send March report?
          </NavItem>
        </NavGroup>
        <NavGroup label="Working">
          <NavItem href="#" leading={<Blob hue="orange" face="wink" />} meta="6m" metaTone="accent">
            Overnight outbound list
          </NavItem>
        </NavGroup>
        <NavGroup label="Chats">
          <NavItem href="#" leading={<Blob hue="amber" face="glasses" />} meta="now" selected>
            Ledger
          </NavItem>
          <NavItem
            href="#"
            leading={<BlobStack members={[{ hue: "teal" }, { hue: "pink", face: "happy" }]} />}
            meta="1h"
          >
            Design pair
          </NavItem>
          <NavItem
            href="#"
            leading={
              <BlobStack
                members={[{ hue: "teal" }, { hue: "pink", face: "happy" }, { hue: "violet", face: "brow" }]}
              />
            }
            meta="1h"
          >
            Offsite crew
          </NavItem>
          <NavItem
            href="#"
            leading={
              <BlobStack
                members={[
                  { hue: "amber", face: "glasses" },
                  { hue: "teal" },
                  { hue: "pink", face: "happy" },
                  { hue: "violet", face: "brow" },
                  { hue: "blue", face: "shades" },
                ]}
              />
            }
            meta="now"
            selected
          >
            Company Wide
          </NavItem>
          <NavItem href="#" leading={<Blob hue="blue" face="shades" />} meta="1d">
            Inbox
          </NavItem>
          <NavItem href="#" leading={<Blob hue="green" face="happy" />} meta="2d">
            Green blob
          </NavItem>
        </NavGroup>
      </Card>

      <Card className="flex-1">
        <CardHeader
          actions={
            <>
              <Button variant="secondary" size="sm">
                Pause
              </Button>
              <Button variant="secondary" size="sm">
                Message
              </Button>
            </>
          }
        >
          <CardTitle>Kit</CardTitle>
        </CardHeader>
        <div className="flex flex-col gap-8 overflow-auto p-10">
          <Row label="Buttons">
            <Button variant="primary">Approve</Button>
            <Button variant="secondary">Always allow</Button>
            <Button variant="ghost">Deny</Button>
            <Button variant="light">Take over</Button>
            <Button variant="danger">Delete Ledger</Button>
            <Button variant="secondary" size="icon" aria-label="Add">
              <PlusIcon />
            </Button>
            <Button variant="ghost" size="icon" aria-label="More">
              <MoreIcon />
            </Button>
          </Row>
          <Row label="Small buttons">
            <Button variant="primary" size="sm">
              <PlusIcon />
              Hire
            </Button>
            <Button variant="secondary" size="sm">
              Take over
            </Button>
            <Button variant="danger" size="sm">
              Pause Ledger
            </Button>
            <Button variant="primary" disabled>
              Disabled
            </Button>
          </Row>
          <Row label="Tags">
            <Tag>
              <WorkingArc />
              Working
            </Tag>
            <Tag tone="attention">Needs you</Tag>
            <Tag tone="positive">
              <CheckIcon />
              Done
            </Tag>
            <Tag tone="quiet">
              <ClockIcon />
              Apr 1, 07:00
            </Tag>
            <Tag tone="negative">Failed</Tag>
            <Tag tone="accent">Org default</Tag>
          </Row>
          <Row label="Tiles">
            <IconTile tone="neutral" size="lg">
              <MailIcon />
            </IconTile>
            <IconTile tone="neutral" size="lg">
              <SheetIcon />
            </IconTile>
            <IconTile tone="active" size="lg">
              <GlobeIcon />
            </IconTile>
            <IconTile tone="accent">
              <MailIcon />
            </IconTile>
            <IconTile>
              <SheetIcon />
            </IconTile>
          </Row>
          {([64, 40, 28, 20] as const).map((size) => (
            <Row key={size} label={`Blobs ${size}`}>
              {(["teal", "amber", "violet", "blue", "orange", "pink", "green"] as const).map((hue, i) => (
                <Blob
                  key={hue}
                  hue={hue}
                  face={["plain", "glasses", "brow", "shades", "wink", "happy", "plain"][i] ?? "plain"}
                  size={size}
                />
              ))}
            </Row>
          ))}
          <div className="flex max-w-[820px] flex-col gap-2.5">
            <SectionLabel note="Defaults come from the org. Changed here: 2">Action rules</SectionLabel>
            <Tray>
              <TrayRow>
                <IconTile tone="accent">
                  <MailIcon />
                </IconTile>
                <RowText title="Send an email" caption="Anything that leaves Pallo Studio" />
                <Segmented aria-label="Send an email" value={level} onValueChange={setLevel} options={levels} />
              </TrayRow>
              <TrayRow height={44}>
                <RowText title="Post to Slack #finance" />
                <Switch defaultChecked />
              </TrayRow>
            </Tray>
          </div>
          <Row label="Inputs">
            <Field className="w-80">
              <FieldLabel>Type Ledger to confirm</FieldLabel>
              <Input placeholder="Ledger" />
            </Field>
            <div className="w-60">
              <Meter label="Budget used" value={0.62} />
            </div>
          </Row>
          <Row label="Tabs">
            <Tabs defaultValue="rules">
              <TabsList>
                <Tab value="memory">Memory</Tab>
                <Tab value="rules">Rules</Tab>
                <Tab value="skills">Skills</Tab>
              </TabsList>
            </Tabs>
            <Tabs defaultValue="computer">
              <TabsList look="pill">
                <Tab look="pill" value="computer">
                  Computer
                </Tab>
                <Tab look="pill" value="tasks">
                  Tasks
                </Tab>
                <Tab look="pill" value="notes">
                  Notes
                </Tab>
              </TabsList>
            </Tabs>
          </Row>
          <Row label="Overlays">
            <Dialog>
              <DialogTrigger render={<Button variant="secondary" />}>Open dialog</DialogTrigger>
              <DialogContent>
                <DialogHeader title="Delete Ledger?" />
                <DialogBody>
                  Their chats stay readable. Their memory notes, routines and rules are removed.
                </DialogBody>
                <Field>
                  <FieldLabel>Type Ledger to confirm</FieldLabel>
                  <Input placeholder="Ledger" />
                </Field>
                <DialogFooter>
                  <Button variant="ghost">Cancel</Button>
                  <Button variant="danger">Delete Ledger</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Popover>
              <PopoverTrigger render={<Button variant="secondary" />}>Open popover</PopoverTrigger>
              <PopoverContent>
                <PopoverHeader title="Notifications" actions={<Button variant="ghost" size="sm">Mark all read</Button>} />
                <PopoverRow>
                  <Blob hue="amber" face="glasses" size={24} />
                  <span className="flex-1 truncate text-[14px]">Ledger wants to send an email</span>
                  <span className="w-8 text-right text-[12px] text-attention">9m</span>
                </PopoverRow>
                <PopoverRow>
                  <Blob hue="pink" face="happy" size={24} />
                  <span className="flex-1 truncate text-[14px]">Echo finished Launch post drafts</span>
                  <span className="w-8 text-right text-[12px] text-text-2">1h</span>
                </PopoverRow>
              </PopoverContent>
            </Popover>
            <Menu>
              <MenuTrigger render={<Button variant="ghost" size="icon" aria-label="More" />}>
                <MoreIcon />
              </MenuTrigger>
              <MenuContent>
                <MenuItem>Rename</MenuItem>
                <MenuItem>Pause</MenuItem>
                <MenuSeparator />
                <MenuItem tone="danger">Delete</MenuItem>
              </MenuContent>
            </Menu>
          </Row>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <SectionLabel>{label}</SectionLabel>
      <div className="flex flex-wrap items-center gap-2.5">{children}</div>
    </div>
  );
}

